import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

interface CartItem {
  id: string;
  name: string;
  artisan: string;
  price: number;
  quantity: number;
  image: string;
}

interface CartPanelProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
}

export const CartPanel = ({ isOpen, onClose, items, onUpdateQuantity, onRemoveItem }: CartPanelProps) => {
  const { t } = useTranslation('cart');
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      {/* Panel */}
      <div className={cn(
        "fixed right-0 top-0 h-full w-96 bg-card shadow-2xl z-50 transform transition-transform duration-300 ease-out",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-display font-semibold">{t('yourCart')}</h2>
              {items.length > 0 && (
                <Badge variant="secondary" className="ml-2">{items.length}</Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close cart">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="font-display font-semibold text-foreground mb-2">{t('emptyCart')}</h3>
                <p className="text-muted-foreground mb-6">{t('emptyCartMessage')}</p>
                <Button variant="terracotta" onClick={onClose}>
                  {t('continueShopping')}
                </Button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">No image</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-display font-semibold text-sm">{item.name}</h4>
                      <p className="text-xs text-muted-foreground">{t('by')} {item.artisan}</p>
                      <p className="text-sm font-semibold text-foreground mt-1">€{item.price}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-sm w-8 text-center">{item.quantity}</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs text-destructive hover:text-destructive"
                        onClick={() => onRemoveItem(item.id)}
                      >
                        {t('remove')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-border p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-display font-semibold">{t('subtotal')}:</span>
                <span className="text-xl font-bold text-foreground">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="space-y-2">
                <Button variant="terracotta" className="w-full" size="lg" asChild>
                  <Link to="/cart">{t('proceedToCheckout')}</Link>
                </Button>
                <Button variant="outline-aegean" className="w-full" onClick={onClose}>
                  {t('continueShopping')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};